// Reusable reason-capture dialog — confirm an action with a typed, logged reason.
// Used for reject (danger), revoke, suspend, etc. (INTERACTIONS.md: destructive
// actions take a logged reason). Esc/backdrop cancel; primary disabled while
// submitting or when a required reason is empty.
//
// `maxLength` states the server's limit and counts down to it (a reason the API would
// refuse can no longer be typed); `confirmPhrase` adds a typed confirm below the reason
// (36E §1.2) — the primary stays disabled until it is typed exactly. If `onConfirm`
// throws, the dialog STAYS OPEN and shows the message inside it (36E §1.3.4): nothing is
// only logged, and the modal never closes on error.
//
// Rides the canonical DS modal shell (ui/dialog.tsx) for the scrim var(--overlay),
// panel var(--surface-raised), var(--shadow-modal), focus-trap and Esc — busy
// blocks dismissal via the Esc/outside guards below.
import React, { useEffect, useId, useState } from 'react';
import { Icon } from '@/components/shared/Icon';
import { PhraseField } from '@/components/shared/TypedConfirmDialog';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

export interface ReasonDialogProps {
  open: boolean;
  title: string;
  body?: string;
  /** What the action is about (e.g. the licences a bulk action covers), shown under the body. */
  details?: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  reasonRequired?: boolean;
  placeholder?: string;
  /** The longest reason the server accepts; the textarea stops there and a counter shows the room left. */
  maxLength?: number;
  /** Typed confirm: the exact text that must be entered (after the reason) before the primary enables. */
  confirmPhrase?: string;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}

export function ReasonDialog({ open, title, body, details, confirmLabel, danger, reasonRequired = true, placeholder = 'Reason (logged to the audit trail)…', maxLength, confirmPhrase, onConfirm, onCancel }: ReasonDialogProps) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const counterId = useId();

  useEffect(() => { if (!open) { setReason(''); setTyped(''); setBusy(false); setError(null); } }, [open]);

  const canConfirm = !busy
    && (!reasonRequired || reason.trim().length > 0)
    && (confirmPhrase === undefined || typed === confirmPhrase);
  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    try { await onConfirm(reason.trim()); }
    catch (e) { setError(e instanceof Error ? e.message : 'The action failed.'); }
    finally { setBusy(false); }
  };
  const accent = danger ? 'var(--status-expired)' : 'var(--accent)';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <DialogContent
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        style={{ maxWidth: 460 }}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          <span style={{ width: 36, height: 36, borderRadius: 9, background: danger ? 'var(--ps-pill-red-bg)' : 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name={danger ? 'alert-triangle' : 'help-circle'} size={18} color={accent} />
          </span>
          <div style={{ flex: 1 }}>
            <DialogTitle style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</DialogTitle>
            {body && <DialogDescription style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>{body}</DialogDescription>}
          </div>
        </div>
        {details}
        <div>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={placeholder} rows={3}
            aria-label="Reason" aria-describedby={maxLength ? counterId : undefined} maxLength={maxLength} disabled={busy}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--surface)', boxSizing: 'border-box', resize: 'vertical' }} />
          {maxLength ? (
            <div id={counterId} style={{ fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>{reason.length} / {maxLength}</div>
          ) : null}
        </div>
        {confirmPhrase !== undefined && (
          <PhraseField phrase={confirmPhrase} value={typed} onChange={setTyped} disabled={busy} />
        )}
        {error && (
          <div role="alert" style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--ps-pill-red-bg)', color: 'var(--ps-pill-red-tx)', fontSize: 12.5, lineHeight: 1.45 }}>{error}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={() => !busy && onCancel()} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-raised)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={submit} disabled={!canConfirm} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: accent, color: '#fff', fontSize: 13, fontWeight: 600, cursor: canConfirm ? 'pointer' : 'not-allowed', opacity: canConfirm ? 1 : 0.5, fontFamily: 'inherit' }}>{busy ? 'Working…' : confirmLabel}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ReasonDialog;
